import { z } from "zod";

// Add-person invitation form schema (Story 14.2, FR75 + UX §13.9). Mirrors the
// backend `invite_clinician` request contract (per-field StringConstraints, no
// model-wide strip): first/last name required (trimmed, 1–255 chars — aligned
// with the BE `max_length=255`, PR #552 review), work email RFC-valid, a
// free-form organization role LABEL chosen from a preset list, an optional
// department + HR ID (blank → omitted at submit so no empty-string is persisted),
// and a user_type radio with EXACTLY two options.
//
// The field contract here is authoritative over mockup screen-34, which drifted:
// the mockup's org-role dropdown, its 3-option user-type <select> (with a
// "Non-clinician" third value), and its "Seat on activation" control are all
// superseded — see the story's Dev Notes → Field contract (W3 reconciliation).

// All bounds mirror the backend `invite_clinician` schema (StringConstraints
// max_length=255; email is the RFC 5321 ceiling of 254). ORGANIZATION_ROLE_LABELS
// is a fixed preset (z.enum), so the role label needs no separate length cap.
const NAME_MAX = 255;
const EMAIL_MAX = 254;
const DEPARTMENT_MAX = 255;
const HR_ID_MAX = 255;

/**
 * Preset organization-role labels offered by the select (mockup screen-34's five
 * values). The chosen value is stored/submitted as the free-form label STRING,
 * NOT an id — FR75: the label is a display value that resolves platform-side to
 * the single `clinician` permission set; it never grants or subdivides access.
 * Exported so the screen renders the option list from a single source of truth.
 */
export const ORGANIZATION_ROLE_LABELS = [
  "Clinician",
  "Clinical Director",
  "OT Manager",
  "Program Lead",
  "Supervisor",
] as const;

/**
 * The two user_type radio values (AC2). "new_learner" → `user_origin=learnworlds_new`
 * (LearnWorlds course path); "legacy_certified" → `user_origin=webapp_legacy`
 * (straight to calibration via web-app credentials). Exactly these two — no third
 * option (the mockup's "Non-clinician (no seat)" is out of scope).
 */
export const USER_TYPE_VALUES = ["new_learner", "legacy_certified"] as const;
export type UserType = (typeof USER_TYPE_VALUES)[number];

const name = (field: string) =>
  z
    .string()
    .trim()
    .min(1, `Enter the clinician's ${field}.`)
    .max(
      NAME_MAX,
      `${field[0].toUpperCase()}${field.slice(1)} must be ${NAME_MAX} characters or fewer.`,
    );

export const addPersonSchema = z.object({
  firstName: name("first name"),
  lastName: name("last name"),
  workEmail: z
    .string()
    .trim()
    .min(1, "Enter the clinician's work email.")
    .max(EMAIL_MAX, `Work email must be ${EMAIL_MAX} characters or fewer.`)
    .email("Enter a valid work email address."),
  // Free-form label chosen from the preset list; required (server: required).
  organizationRoleLabel: z
    .enum(ORGANIZATION_ROLE_LABELS, {
      errorMap: () => ({ message: "Select an organization role." }),
    }),
  // Optional bounded text; blank stays "" here and is omitted at submit time.
  department: z
    .string()
    .trim()
    .max(DEPARTMENT_MAX, `Department must be ${DEPARTMENT_MAX} characters or fewer.`),
  hrId: z
    .string()
    .trim()
    .max(HR_ID_MAX, `HR ID must be ${HR_ID_MAX} characters or fewer.`),
  // Radio group; "" is the un-chosen state and is rejected so a choice is forced.
  userType: z.enum(USER_TYPE_VALUES, {
    errorMap: () => ({ message: "Choose the user type." }),
  }),
});

export type AddPersonValues = z.infer<typeof addPersonSchema>;
